import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { AccountSetupDialogStore } from '../account-setup-dialog.store';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { filter, take, takeUntil } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { AccountConfigType } from 'src/app/models/account-config-type';
import { AccountMappingFieldComponent } from './account-mapping-field/account-mapping-field.component';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

@Component({
    selector: 'generic-account-mapping',
    templateUrl: './generic-account-mappings.component.html',
    standalone: true,
    imports: [NgIf, ReactiveFormsModule, NgFor, AccountMappingFieldComponent, AsyncPipe]
})
export class GenericAccountMappingComponent implements OnInit {
  form: FormGroup;

  accountsToMap$ = this.store.hlAccountsToBeMapped$;
  accountsAvailable$ = this.store.accounts$;
  loading$ = this.store.loadingAccounts$;

  @Output() newFormGroup = new EventEmitter<FormGroup>();

  constructor(
    private store: AccountSetupDialogStore,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.store.getAccounts();
    combineLatest([
      this.store.hlAccountsToBeMapped$,
      this.store.configuredAccounts$
    ])
      .pipe(takeUntil(this.store.destroy$))
      .subscribe(([accounts, values]) => this.setFormGroup(accounts, values));
  }

  private setFormGroup(accounts: any, values) {
    const { revenue, payment, expenses } = accounts;
    this.form = this.fb.group(
      [...revenue, ...payment, ...expenses].reduce(
        (acc, curr) => ({
          ...acc,
          [curr]: this.fb.control(values[curr], Validators.required)
        }),
        {}
      )
    );
    this.setSuggestedAccounts(values);
    this.newFormGroup.emit(this.form);
  }

  private setSuggestedAccounts(savedValues: any) {
    if (Object.keys(savedValues).length === 0) {
      // saved values not loaded yet
      return;
    }

    const allSavedValuesFalsey = Object.values(savedValues).every(
      (savedValues) => !savedValues
    );
    if (!allSavedValuesFalsey) {
      // if any of the saved values are truthy, we assume the user has already set up the accounts, do not attempt to populate defaults now
      return;
    }
    this.accountsAvailable$
      .pipe(
        filter((accounts) => Boolean(accounts.length)),
        take(1),
        takeUntil(this.store.destroy$)
      )
      .subscribe((accounts) => {
        const defaultSalesAccount = accounts.find((account) =>
          account.name.startsWith('Sales')
        );
        const defaultHiresAccount = accounts.find(
          (account) => account.name.indexOf('Hire') > -1
        );
        const defaultDeliveryAccount = accounts.find(
          (account) => account.name.indexOf('Deliver') > -1
        );
        const defaultPurchasesAccount = accounts.find(
          (account) => account.name.indexOf('Purchases') > -1
        );
        const defaultBankAccount = accounts.find((account) =>
          account.name.startsWith('Bank')
        );
        const defaultCashAccount = accounts.find((account) =>
          account.name.startsWith('Cash')
        );
        if (defaultSalesAccount) {
          if (!savedValues[AccountConfigType.RETAIL_SALES_REVENUE_CODE]) {
            this.form
              .get(AccountConfigType.RETAIL_SALES_REVENUE_CODE)
              .setValue(defaultSalesAccount.id);
          }
          if (!savedValues[AccountConfigType.HIRE_SALES_REVENUE_CODE]) {
            if (defaultHiresAccount) {
              this.form
                .get(AccountConfigType.HIRE_SALES_REVENUE_CODE)
                .setValue(defaultHiresAccount.id);
            } else {
              this.form
                .get(AccountConfigType.HIRE_SALES_REVENUE_CODE)
                .setValue(defaultSalesAccount.id);
            }
          }
          if (!savedValues[AccountConfigType.DELIVERY_REVENUE_CODE]) {
            if (defaultDeliveryAccount) {
              this.form
                .get(AccountConfigType.DELIVERY_REVENUE_CODE)
                .setValue(defaultDeliveryAccount.id);
            } else {
              this.form
                .get(AccountConfigType.DELIVERY_REVENUE_CODE)
                .setValue(defaultSalesAccount.id);
            }
          }
          if (!savedValues[AccountConfigType.RETAIL_PURCHASES_CODE]) {
            if (defaultPurchasesAccount) {
              this.form
                .get(AccountConfigType.RETAIL_PURCHASES_CODE)
                .setValue(defaultPurchasesAccount.id);
            }
          }
          if (!savedValues[AccountConfigType.CONSUMABLE_SALES_REVENUE_CODE]) {
            this.form
              .get(AccountConfigType.CONSUMABLE_SALES_REVENUE_CODE)
              .setValue(defaultSalesAccount.id);
          }
        }
        if (
          !savedValues[AccountConfigType.BANK_TRANSFER_PAYMENTS] &&
          defaultBankAccount
        ) {
          this.form
            .get(AccountConfigType.BANK_TRANSFER_PAYMENTS)
            .setValue(defaultBankAccount.id);
        }

        if (
          !savedValues[AccountConfigType.CARD_PAYMENTS] &&
          defaultCashAccount
        ) {
          this.form
            .get(AccountConfigType.CARD_PAYMENTS)
            .setValue(defaultCashAccount.id);
        }
      });
  }
}
